/**
 * PersonaMemoryPanel — UI for viewing/managing AI persona memory notes.
 * GM-only panel integrated into AiChatPanel. [STYLE_TODO]
 */

import { useEffect, useState } from 'react';
import { Brain, Trash2, BookOpen, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import type { UsePersonaMemoryReturn } from '../hooks/usePersonaMemory';
import { useI18n } from '../i18n';
import Markdown from 'react-markdown';

interface PersonaMemoryPanelProps {
  memory: UsePersonaMemoryReturn;
  onClose: () => void;
  /** Callback to save chronicle text as a journal handout */
  onSaveChronicle?: (text: string) => void;
}

export default function PersonaMemoryPanel({ memory, onClose, onSaveChronicle }: PersonaMemoryPanelProps) {
  const { t } = useI18n();

  // Load notes on mount
  useEffect(() => {
    memory.loadNotes();
  }, [memory.loadNotes]);

  const tagLabel = (tag: string) => {
    return tag === 'intention' ? '🎯' : '💭';
  };

  // Chronicle view
  if (memory.isChronicleStreaming || memory.chronicleResult) {
    return (
      <div className="persona-memory-panel flex flex-col gap-2 p-3" data-component="PersonaMemoryPanel">
        {/* [STYLE_TODO] Chronicle streaming display */}
        <div className="flex items-center justify-between">
          <h3 className="text-ui font-medium flex items-center gap-1.5">
            <BookOpen size={14} />
            <span>{t('ai.chronicle') ?? 'Chronicle'}</span>
          </h3>
          <Button variant="ghost" size="sm" onClick={memory.clearChronicle}>
            {t('common.close') ?? 'Close'}
          </Button>
        </div>
        <div className="text-ui prose prose-sm prose-invert max-w-none">
          <Markdown>{memory.chronicleText || '...'}</Markdown>
        </div>
        {memory.isChronicleStreaming && (
          <div className="text-meta text-muted-foreground animate-pulse">
            {t('ai.generating') ?? 'Generating...'}
          </div>
        )}
        {memory.chronicleResult && onSaveChronicle && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1"
            onClick={() => onSaveChronicle(memory.chronicleResult!)}
          >
            <BookOpen size={14} className="mr-1" />
            {t('ai.saveAsJournal') ?? 'Save as Journal Entry'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="persona-memory-panel flex flex-col gap-2 p-3" data-component="PersonaMemoryPanel">
      {/* [STYLE_TODO] Header with close button */}
      <div className="flex items-center justify-between">
        <h3 className="text-ui font-medium flex items-center gap-1.5">
          <Brain size={14} />
          <span>{t('ai.personaMemory') ?? 'Persona Memory'}</span>
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t('common.close') ?? 'Close'}
        </Button>
      </div>

      {/* Notes list or empty state */}
      {memory.isLoading ? (
        <div className="text-meta text-muted-foreground">{t('common.loading') ?? 'Loading...'}</div>
      ) : memory.notes.length === 0 ? (
        <div className="text-meta text-dim py-4 text-center">
          {t('ai.noPersonaMemory') ?? 'No memories yet. Tia will form impressions after conversations.'}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
          {memory.notes.map(note => (
            <div
              key={note.id}
              className="persona-memory-note flex items-start gap-2 rounded border border-line/50 bg-panel-2/50 p-2 text-meta group"
            >
              <span className="shrink-0 mt-0.5" title={note.tag}>
                {tagLabel(note.tag)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-fg leading-relaxed">{note.text}</p>
                <span className="text-dim text-label">
                  {note.createdAt.slice(0, 10)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 shrink-0 w-6 p-0"
                onClick={() => memory.deleteNote(note.id)}
                title={t('common.delete') ?? 'Delete'}
              >
                <Trash2 size={12} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Chronicle & Epilogue buttons */}
      {memory.notes.length >= 5 && (
        <div className="flex gap-1.5 mt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => memory.requestChronicle(false)}
            disabled={memory.isChronicleStreaming}
          >
            <Sparkles size={14} className="mr-1" />
            {t('ai.requestChronicle') ?? 'Request Chronicle'}
          </Button>
          {memory.notes.length >= 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => memory.requestChronicle(true)}
              disabled={memory.isChronicleStreaming}
            >
              <BookOpen size={14} className="mr-1" />
              {t('ai.requestEpilogue') ?? 'Epilogue'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
